import {
    AutosizeTextarea,
    AutosizeTextAreaRef,
    Button,
    MindyLogoDark,
    MindyLogoLight,
    Separator,
    useTheme,
} from '@quadminds/mindkit';
import { ArrowUp } from 'lucide-react';
import * as React from 'react';
import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import mindyIcon from '../../../../assets/img/mindy-icon.svg';

export interface UploadedFile {
    name: string;
    size: number;
    type: string;
    preview: string;
    file: File;
}

interface InputProps {
    chatInputValue: string;
    setChatInputValue: React.Dispatch<React.SetStateAction<string>>;
    files: UploadedFile[];
    setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
    onSendMessage: () => void;
    isLoading: boolean;
    expanded?: boolean;
    onFocus?: () => void;
    // Cuando viene ?new_header=true en la URL del embed, usa el estilo pill
    // de product/home (ver TopBar.tsx) en lugar del recuadro actual. Con el
    // param ausente el estilo de siempre queda intacto — así saas/header.php
    // puede activarlo o revertirlo sin tocar este componente.
    newHeader?: boolean;
}

export const Input = forwardRef(
    (
        {
            chatInputValue,
            setChatInputValue,
            onSendMessage,
            isLoading = false,
            expanded = true,
            onFocus,
            newHeader = false,
        }: InputProps,
        ref: Ref<{ focusTextArea(): void; isTextAreaFocused(): boolean; isTextAreaDisabled(): boolean }>,
    ) => {
        const textAreaRef = React.useRef<AutosizeTextAreaRef | null>(null);
        const { t } = useTranslation();
        const { theme } = useTheme();

        // Placeholders dinámicos para el estado no expandido
        const dynamicPlaceholders = useMemo(
            () => ['¿Cuáles son mis rutas de hoy?', '¿Cuál es el estado de la operación del día?'],
            [],
        );

        const [currentPlaceholder, setCurrentPlaceholder] = useState('');
        const [placeholderIndex, setPlaceholderIndex] = useState(0);
        const [charIndex, setCharIndex] = useState(0);
        const [isDeleting, setIsDeleting] = useState(false);

        // Efecto para el placeholder animado cuando no está expandido
        useEffect(() => {
            if (expanded) {
                setCurrentPlaceholder(t('chat.inputPlaceholder'));
                return;
            }

            const currentText = dynamicPlaceholders[placeholderIndex];
            const typingSpeed = isDeleting ? 25 : 50;
            const pauseBeforeDelete = 2000;
            const pauseBeforeNext = 500;

            let delay = typingSpeed;

            if (!isDeleting && charIndex === currentText.length) {
                delay = pauseBeforeDelete;
            } else if (isDeleting && charIndex === 0) {
                delay = pauseBeforeNext;
            }

            const timeout = setTimeout(() => {
                if (!isDeleting && charIndex < currentText.length) {
                    // Escribiendo
                    setCurrentPlaceholder(currentText.substring(0, charIndex + 1));
                    setCharIndex(charIndex + 1);
                } else if (!isDeleting && charIndex === currentText.length) {
                    // Cambiar a modo borrado
                    setIsDeleting(true);
                } else if (isDeleting && charIndex > 0) {
                    // Borrando
                    setCurrentPlaceholder(currentText.substring(0, charIndex - 1));
                    setCharIndex(charIndex - 1);
                } else if (isDeleting && charIndex === 0) {
                    // Cambiar al siguiente placeholder
                    setIsDeleting(false);
                    setPlaceholderIndex((placeholderIndex + 1) % dynamicPlaceholders.length);
                }
            }, delay);

            return () => clearTimeout(timeout);
        }, [charIndex, isDeleting, placeholderIndex, expanded, dynamicPlaceholders, t]);

        useImperativeHandle(ref, () => ({
            isTextAreaDisabled: () => textAreaRef.current?.textArea.disabled ?? false,
            isTextAreaFocused: () => textAreaRef.current?.textArea === document.activeElement,
            focusTextArea: () => {
                textAreaRef.current?.textArea.focus();
            },
        }));

        const collapsedNewHeaderClasses =
            'rounded-full border-gray-qm-100 bg-gray-qm-50/60 hover:border-primary-qm-500/45 hover:bg-white dark:border-gray-qm-600 dark:bg-gray-qm-900';
        const collapsedDefaultClasses = 'border-gray-qm-300 dark:border-gray-qm-600 dark:border-0 dark:bg-gray-qm-900';

        return (
            <div
                className={`
            relative transition  flex items-center border pl-2 pr-1.5
            w-full
            ${expanded ? 'py-2 !pr-3 rounded-lg' : newHeader ? collapsedNewHeaderClasses : collapsedDefaultClasses}
        `}
            >
                {!expanded &&
                    (newHeader ? (
                        <img src={mindyIcon} alt="Mindy" className="h-7 w-7 mr-2 shrink-0 rounded-full" />
                    ) : (
                        <>
                            <img
                                src={theme === 'dark' ? MindyLogoLight : MindyLogoDark}
                                alt="Mindy"
                                className="h-8 mr-2"
                            />
                            <Separator orientation="vertical" className="h-4" />
                        </>
                    ))}
                <input className="hidden" />
                <AutosizeTextarea
                    ref={textAreaRef}
                    placeholder={currentPlaceholder}
                    maxHeight={42}
                    className="text-md border-0 focus-visible:!ring-0 focus-visible:!ring-offset-0 !bg-transparent flex-grow pt-[9px] items-center !min-h-[42px]"
                    value={chatInputValue}
                    onChange={(e) => setChatInputValue(e.target.value)}
                    onFocus={onFocus}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onSendMessage();
                        }
                    }}
                    disabled={isLoading}
                />
                <Button
                    className={`rounded-full !h-8 !w-8 !min-w-8 !min-h-8 ${
                        newHeader
                            ? 'bg-primary-qm-500 text-white hover:bg-primary-qm-600'
                            : 'dark:bg-white dark:text-primary hover:dark:bg-tertiary-qm-100'
                    }`}
                    size="icon"
                    disabled={isLoading || !chatInputValue}
                    onClick={onSendMessage}
                    data-testid="send-button"
                >
                    <ArrowUp />
                </Button>
            </div>
        );
    },
);

export default Input;
